"use client";

import { useEffect } from "react";

/**
 * Progressive-enhancement scroll reveal (Vanta-style fade-up).
 *
 * Elements marked `data-reveal` are visible by default (no-JS / crawler safe).
 * On mount — and only when the user hasn't asked to reduce motion — we add
 * `reveal-ready` to <html>, which switches those elements to their hidden
 * start state, then reveal each one with `in-view` as it scrolls into view.
 * Optional per-element stagger via inline `--reveal-delay`.
 */
export function ScrollReveal() {
  useEffect(() => {
    const root = document.documentElement;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (typeof IntersectionObserver === "undefined") return;

    root.classList.add("reveal-ready");

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
            io.unobserve(entry.target);
          }
        }
      },
      // threshold 0, not a ratio: an element taller than the viewport can
      // never reach a ratio threshold and would stay hidden forever.
      { rootMargin: "0px 0px -8% 0px", threshold: 0 }
    );

    const observeAll = () => {
      document
        .querySelectorAll("[data-reveal]:not(.in-view)")
        .forEach((el) => io.observe(el));
    };
    observeAll();

    // This component lives in the root layout, which survives client-side
    // navigations — the effect runs once per full page load. Each page swap
    // mounts fresh [data-reveal] elements that start hidden, so watch the DOM
    // and observe them as they appear; otherwise they never reveal.
    const mo = new MutationObserver(observeAll);
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, []);

  return null;
}
